import React from 'react';

import { Controller, useForm } from 'react-hook-form';

import {
	Box,
	Button,
	createModal,
	DateInput as DatePickerInput,
	Flex,
	Input,
	Stack,
	TextInput,
	useDisclosure,
	useModalControl,
} from '@tf/ui';
import { D, fmt } from '@tf/utils';

import type {
	AdhocSearchParams,
	EntityAdhocSearchParams,
	PersonAdhocSearchParams,
} from '../../../../api/backgroundCheck';

const PersonSearchParamsForm: React.FC<{
	initialValue?: PersonAdhocSearchParams;
	isReadOnly: boolean;
	onSubmit: (data: PersonAdhocSearchParams) => void;
	onClose: () => void;
}> = ({ initialValue, onSubmit, onClose, isReadOnly }) => {
	const { control, handleSubmit, formState } = useForm<PersonAdhocSearchParams>({
		defaultValues: initialValue ?? {},
		mode: 'onSubmit',
	});

	const submit = handleSubmit((data) => {
		if (Object.values(formState.errors).length === 0) {
			onSubmit(data);
			onClose();
		}
	});

	return (
		<Stack gap={12}>
			<Controller
				name="firstName"
				control={control}
				rules={{ required: true }}
				render={({ field, fieldState }) => (
					<Input.Wrapper label="First name" labelProps={{ required: true }}>
						<TextInput disabled={isReadOnly} {...field} error={Boolean(fieldState.error)} />
					</Input.Wrapper>
				)}
			/>
			<Controller
				name="middleName"
				control={control}
				render={({ field, fieldState }) => (
					<TextInput
						disabled={isReadOnly}
						label="Middle name"
						{...field}
						error={Boolean(fieldState.error)}
					/>
				)}
			/>
			<Controller
				name="lastName"
				control={control}
				rules={{ required: true }}
				render={({ field, fieldState }) => (
					<Input.Wrapper label="Last name" labelProps={{ required: true }}>
						<TextInput disabled={isReadOnly} {...field} error={Boolean(fieldState.error)} />
					</Input.Wrapper>
				)}
			/>
			<Controller
				name="birthDate"
				control={control}
				render={({ field: { onChange, value }, fieldState: { error } }) => (
					<Input.Wrapper label="Date of birth">
						<DatePickerInput
							disabled={isReadOnly}
							value={typeof value === 'string' ? D.jsDateFromString(value as string) : undefined}
							onChange={(v) => {
								const nextValue = v ? fmt.toDate(v, { preset: 'date_struct' }) : null;
								onChange(nextValue);
							}}
							variant="filled"
							valueFormat="DD.MM.YYYY"
							locale="en"
							placeholder="Select Date..."
							popoverProps={{ zIndex: 1001, position: 'bottom' }}
							error={Boolean(error)}
						/>
					</Input.Wrapper>
				)}
			/>
			<Flex gap="sm" pt="0.75rem">
				<Button fullWidth variant="light" onClick={onClose}>
					Close
				</Button>
				{!isReadOnly ? (
					<Button fullWidth onClick={submit}>
						Set
					</Button>
				) : null}
			</Flex>
		</Stack>
	);
};

const EntitySearchParamsForm: React.FC<{
	initialValue?: EntityAdhocSearchParams;
	isReadOnly: boolean;
	onSubmit: (data: EntityAdhocSearchParams) => void;
	onClose: () => void;
}> = ({ initialValue, onSubmit, onClose, isReadOnly }) => {
	const { control, handleSubmit, formState } = useForm<EntityAdhocSearchParams>({
		defaultValues: initialValue,
		mode: 'onSubmit',
	});

	const submit = handleSubmit((data) => {
		if (Object.values(formState.errors).length === 0) {
			onSubmit(data);
			onClose();
		}
	});

	return (
		<Stack gap={12}>
			<Controller
				name="entityName"
				control={control}
				rules={{ required: true }}
				render={({ field, fieldState }) => {
					return (
						<Input.Wrapper label="Entity name" required labelProps={{ required: true }}>
							<TextInput disabled={isReadOnly} error={Boolean(fieldState.error)} {...field} />
						</Input.Wrapper>
					);
				}}
			/>
			<Flex gap="sm" pt="0.75rem">
				<Button fullWidth variant="light" onClick={onClose}>
					Close
				</Button>
				{!isReadOnly ? (
					<Button fullWidth onClick={submit}>
						Set
					</Button>
				) : null}
			</Flex>
		</Stack>
	);
};

interface SearchParamsProps {
	isReadOnly: boolean;
	isIndividual: boolean;
	initialValue?: AdhocSearchParams;
	onSubmit: (data: AdhocSearchParams) => void;
}

const SearchParamsModal = createModal<SearchParamsProps>(
	({ isIndividual, isReadOnly, initialValue, onSubmit }) => {
		const { onClose } = useModalControl();

		return (
			<Box p="1rem">
				{isIndividual ? (
					<PersonSearchParamsForm
						initialValue={initialValue?.person}
						isReadOnly={isReadOnly}
						onSubmit={(person) => onSubmit({ person })}
						onClose={onClose}
					/>
				) : (
					<EntitySearchParamsForm
						initialValue={initialValue?.entity}
						isReadOnly={isReadOnly}
						onSubmit={(entity) => onSubmit({ entity })}
						onClose={onClose}
					/>
				)}
			</Box>
		);
	}
);

export const EditSearchParamsButton: React.FC<SearchParamsProps> = ({
	isReadOnly,
	isIndividual,
	initialValue,
	onSubmit,
}) => {
	const searchParamsModal = useDisclosure();

	return (
		<>
			<Button
				size="xs"
				variant="subtle"
				sx={({ colors }) => ({
					'&:hover': {
						backgroundColor: 'transparent',
						color: colors.brand[7],
					},
				})}
				onClick={searchParamsModal.open}
			>
				{isReadOnly ? 'Show data for searching' : 'Edit data for searching'}
			</Button>
			<SearchParamsModal
				title="Search parameters"
				opened={searchParamsModal.isOpen}
				onClose={searchParamsModal.close}
				contentProps={{ isIndividual, isReadOnly, initialValue, onSubmit }}
			/>
		</>
	);
};
